import React, { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { APP_CONSTANTS } from 'src/consumer/constants'

import { ChevronDown } from './icons'
import { CAREGIVER_NAV, DOCTOR_NAV } from './roleData'
import { Container } from './sections'

// ── NavDropdown ──────────────────────────────────────────
// Small hover+click dropdown used in the top bar for Doctors / Care-Givers.

const NavDropdown = ({ label, items }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false)
    }
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('click', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('click', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <div
      className="as-dd"
      data-open={open}
      ref={wrapRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="as-dd-button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
        <ChevronDown />
      </button>
      <div className="as-dd-panel" role="menu">
        <div className="as-dd-card">
          {items.map(({ to, label: l, sub }) => (
            <Link
              key={to}
              to={to}
              role="menuitem"
              className="as-dd-item"
              onClick={() => setOpen(false)}
            >
              <div className="as-dd-item-label">{l}</div>
              {sub ? <div className="as-dd-item-sub">{sub}</div> : null}
            </Link>
          ))}
        </div>
      </div>
    </div>
  )
}

// ── TopBar ───────────────────────────────────────────────

// Always-floating header. We track whether the page has been scrolled past
// a small threshold so we can render an elevated, condensed variant of the
// header (more blur + shadow) once the user has started scrolling. The
// header itself never hides — it floats over content in both directions.
function useScrolledPastThreshold({ threshold = 8 } = {}) {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    if (typeof window === 'undefined') return undefined
    let rafId = 0
    let queued = false

    const update = () => {
      queued = false
      setScrolled((window.scrollY || 0) > threshold)
    }

    const onScroll = () => {
      if (queued) return
      queued = true
      rafId = window.requestAnimationFrame(update)
    }

    // Sync once on mount (handles SSR hydrate + anchor scroll restores).
    update()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      if (rafId) window.cancelAnimationFrame(rafId)
    }
  }, [threshold])
  return scrolled
}

export const TopBar = ({ onLogin, contact, onContactClick }) => {
  const [mobileOpen, setMobileOpen] = useState(false)
  const scrolled = useScrolledPastThreshold()
  const location = useLocation()

  // Close the mobile drawer whenever the route changes. Relying on the
  // route change (rather than a blanket onClick on the <nav>) means that
  // tapping inside <details>/<summary> to expand a dropdown doesn't
  // accidentally dismiss the drawer, and Link navigation isn't racing
  // against its own parent unmount.
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])

  return (
    <header
      className="as-topbar"
      role="banner"
      data-scrolled={scrolled ? 'true' : 'false'}
    >
      <Container>
        <div className="as-topbar-inner">
          <Link to="/" className="as-brand" aria-label="AUMHUM — home">
            <span className="as-brand-mark" aria-hidden="true">
              <img
                src={APP_CONSTANTS.APP_LOGO_LINK}
                alt=""
                loading="eager"
                decoding="async"
                width={32}
                height={32}
              />
            </span>
            <span>AUMHUM</span>
          </Link>
          <nav className="as-nav" aria-label="Primary">
            <Link to="/care/start">Get Help</Link>
            <NavDropdown label="Doctors" items={DOCTOR_NAV} />
            <NavDropdown label="Care-Givers" items={CAREGIVER_NAV} />
            <Link to="/self-care">Self-care</Link>
            <Link to="/how-care-works">How Care Works</Link>
            <Link to="/students">Students</Link>
            <Link to="/aumhum-for-workplace">For Organizations</Link>
          </nav>
          <div className="as-topbar-actions">
            {contact ? (
              <button
                type="button"
                className="as-header-phone"
                onClick={onContactClick}
                aria-label={`Call AUMHUM at ${contact.display}`}
                title="Connect with our team"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
                </svg>
                <span className="as-header-phone-num">{contact.display}</span>
              </button>
            ) : null}
            <button type="button" className="as-login-btn" onClick={onLogin}>
              Login
            </button>
            <Link
              to="/care/start"
              className="as-btn as-btn-primary as-cta-desktop"
            >
              Start care
            </Link>
            <button
              type="button"
              className="as-mobile-toggle as-login-btn"
              aria-expanded={mobileOpen}
              aria-controls="aumhum-mobile-menu"
              aria-label="Toggle navigation"
              onClick={() => setMobileOpen((m) => !m)}
            >
              Menu
            </button>
          </div>
        </div>
        {mobileOpen ? (
          <nav
            id="aumhum-mobile-menu"
            className="as-mobile-menu"
            aria-label="Mobile"
            onClick={(e) => {
              // Close the drawer only when the user actually tapped a
              // navigation link. A tap on <summary> must toggle <details>
              // open, and a tap on empty space shouldn't dismiss either —
              // the previous blanket handler made dropdowns unusable on
              // mobile because every tap collapsed the drawer mid-frame.
              if (e.target.closest && e.target.closest('a')) {
                setMobileOpen(false)
              }
            }}
          >
            <Link to="/care/start">Get Help</Link>
            <details>
              <summary>Doctors</summary>
              <div>
                {DOCTOR_NAV.map((d) => (
                  <Link key={d.to} to={d.to}>
                    {d.label}
                  </Link>
                ))}
              </div>
            </details>
            <details>
              <summary>Care-Givers</summary>
              <div>
                {CAREGIVER_NAV.map((c) => (
                  <Link key={c.to} to={c.to}>
                    {c.label}
                  </Link>
                ))}
              </div>
            </details>
            <Link to="/self-care">Self-care</Link>
            <Link to="/how-care-works">How Care Works</Link>
            <Link to="/students">Students</Link>
            <Link to="/aumhum-for-workplace">For Organizations</Link>
            <Link to="/aumhum-for-clinics">For Clinics &amp; Rehab</Link>
            <Link to="/join">Join our network</Link>
          </nav>
        ) : null}
      </Container>
    </header>
  )
}

// ── Footer ───────────────────────────────────────────────

export const Footer = () => (
  <footer className="as-footer" role="contentinfo">
    <Container>
      <div className="as-footer-grid">
        <div>
          <div className="as-brand">
            <span className="as-brand-mark" aria-hidden="true">
              <img
                src={APP_CONSTANTS.APP_LOGO_LINK}
                alt=""
                loading="lazy"
                decoding="async"
                width={32}
                height={32}
              />
            </span>
            <span>AUMHUM</span>
          </div>
          <p className="as-p as-mt-3">
            Clinical mental healthcare with integrated psychiatric and endocrine care,
            delivered as a continuous care system.
          </p>
        </div>
        <div>
          <h4>Care</h4>
          <ul>
            <li><Link to="/care/start">Get help</Link></li>
            <li><Link to="/how-care-works">How care works</Link></li>
            <li><Link to="/therapy-psychiatry">Book a consultation</Link></li>
            <li><Link to="/self-care">Self-care app</Link></li>
          </ul>
        </div>
        <div>
          <h4>Clinicians</h4>
          <ul>
            <li><Link to="/doctors/psychiatrist">Doctors</Link></li>
            <li><Link to="/care-givers/clinical-psychologist">Care-Givers</Link></li>
            <li><Link to="/students">Students — Experience Program</Link></li>
            <li><Link to="/join">Join our network</Link></li>
          </ul>
        </div>
        <div>
          <h4>Partners &amp; company</h4>
          <ul>
            <li><Link to="/aumhum-for-workplace">For organizations</Link></li>
            <li><Link to="/aumhum-for-clinics">For clinics &amp; rehab</Link></li>
            <li><Link to="/about">About</Link></li>
            <li><Link to="/privacy">Privacy</Link></li>
            <li><Link to="/terms">Terms</Link></li>
          </ul>
        </div>
      </div>
      <div className="as-footer-bottom">
        <p>© {new Date().getFullYear()} AUMHUM. Clinical mental healthcare.</p>
        <p>
          Care is delivered by licensed clinicians in accordance with applicable regulations.
        </p>
      </div>
    </Container>
  </footer>
)
